import React, { useEffect, useState } from 'react';

import { getPayoutDetails, savePayoutDetails } from '../../../api_handlers/invoice';
import { readReturnTo } from '../../invoices/lib/returnTo';
import { Alert, KIT_CSS, useStyleSheet } from '../../invoices/kit';
import PayoutFields, {
  EMPTY_PAYOUT,
  PAYOUT_CSS,
  payoutPayload,
  validatePayout,
} from '../../invoices/PayoutFields';

/**
 * The creator's payout details — bank account, PAN, GST and MSME — captured
 * once and reused on every invoice. Invoices cannot be raised until this is
 * filled in, because finance has nowhere to send the money otherwise.
 */
export default function Payout() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('pf-fields', PAYOUT_CSS);

  const [values, setValues] = useState(EMPTY_PAYOUT);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState(false);
  const [onFile, setOnFile] = useState(false);
  // Where the creator was before this detour, if anywhere.
  const back = readReturnTo(typeof window === 'undefined' ? '' : window.location.search);

  useEffect(() => {
    let alive = true;
    getPayoutDetails()
      .then((data) => {
        if (!alive || !data) return;
        setOnFile(true);
        setValues({
          ...EMPTY_PAYOUT,
          account_holder_name: data.account_holder_name || '',
          account_number: data.account_number || '',
          ifsc_code: data.ifsc_code || '',
          bank_name: data.bank_name || '',
          pan_card_no: data.pan_card_no || '',
          gst_number: data.gst_number || '',
          is_msme: Boolean(data.is_msme),
          msme_udyam_number: data.msme_udyam_number || '',
          msme_category: data.msme_category || '',
        });
      })
      .catch((e) => alive && setMessage(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onChange = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setSaved(false);
    if (errors[key]) setErrors((current) => ({ ...current, [key]: null }));
  };

  const onSave = async () => {
    const found = validatePayout(values);
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    setMessage('');
    try {
      await savePayoutDetails(payoutPayload(values), files);
      setSaved(true);
      setOnFile(true);
      setFiles({});
    } catch (e) {
      setErrors(e.fieldErrors || {});
      setMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inf-panel iv-root">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <p className="inf-panel-title">Payout details</p>
          <p className="inf-panel-sub">
            Where we pay you. Saved once and used on every invoice you raise.
          </p>
        </div>
        <button
          type="button"
          className="iv-btn iv-btn--primary"
          onClick={onSave}
          disabled={loading || saving}
        >
          {saving ? <span className="iv-spin" /> : null}
          {onFile ? 'Save changes' : 'Save'}
        </button>
      </div>

      {message ? (
        <div style={{ marginBottom: 16 }}>
          <Alert>{message}</Alert>
        </div>
      ) : null}
      {saved ? (
        <div className="pay-saved" role="status">
          <div>
            <p className="pay-saved__title">Payout details saved</p>
            <p className="pay-saved__text">
              Invoices you raise from now on will be paid to this account.
            </p>
          </div>
          {/* Finishing here used to strand the creator on a settings page with
              the invoice they came to raise abandoned. */}
          {back ? (
            <a className="iv-btn iv-btn--primary" href={back}>
              Continue
            </a>
          ) : (
            <a className="iv-btn iv-btn--primary" href="/influencer/invoices">
              Go to invoices
            </a>
          )}
        </div>
      ) : null}

      <p className="pay-privacy">
        <span className="pay-privacy__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        Used only to pay you. Visible to our finance team, never to brands.
      </p>

      {loading ? (
        <div className="iv-sk" style={{ height: 280, borderRadius: 12 }} />
      ) : (
        <PayoutFields
          values={values}
          errors={errors}
          onChange={onChange}
          files={files}
          onFile={(key, file) => setFiles((current) => ({ ...current, [key]: file }))}
          disabled={saving}
        />
      )}
    </div>
  );
}
